import * as React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const FADE_UP_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', duration: 0.8 } },
};

const STAGGER_CONTAINER_VARIANTS = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export function EthicalHero({
  title,
  subtitle,
  ctaLabel,
  ctaHref,
  ctaTarget,
  ctaRel,
  features,
  className,
  titleClassName,
  subtitleClassName,
  ctaClassName,
  cardClassName,
  cardImageClassName,
  cardTitleClassName,
}) {
  return (
    <motion.section
      initial="hidden"
      animate="show"
      variants={STAGGER_CONTAINER_VARIANTS}
      className={cn('container mx-auto max-w-6xl px-4 py-16 sm:py-24', className)}
    >
      <div className="mx-auto max-w-3xl text-center">
        <motion.h1
          variants={FADE_UP_VARIANTS}
          className={cn(
            'text-3xl font-bold tracking-tight text-foreground sm:text-5xl',
            titleClassName
          )}
        >
          {title}
        </motion.h1>

        <motion.p
          variants={FADE_UP_VARIANTS}
          className={cn('mt-6 text-lg leading-8 text-muted-foreground', subtitleClassName)}
        >
          {subtitle}
        </motion.p>

        <motion.div variants={FADE_UP_VARIANTS} className="mt-10">
          <Button size="lg" asChild className={ctaClassName}>
            <a href={ctaHref} target={ctaTarget} rel={ctaRel}>
              {ctaLabel}
            </a>
          </Button>
        </motion.div>
      </div>

      <motion.div
        variants={STAGGER_CONTAINER_VARIANTS}
        className="mt-16 grid grid-cols-1 gap-6 sm:mt-24 md:grid-cols-3"
      >
        {features.map((feature) => {
          const handleClick = (e) => {
            if (feature.onClick) {
              e.preventDefault();
              feature.onClick();
            }
          };
          return (
            <motion.a
              key={feature.id}
              href={feature.href || '#'}
              onClick={handleClick}
              aria-label={feature.title}
              variants={FADE_UP_VARIANTS}
              whileHover={{ scale: 1.03 }}
              transition={{ type: 'spring', stiffness: 300 }}
              className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-xl"
            >
              <Card
                className={cn(
                  'group h-full overflow-hidden rounded-xl shadow-sm transition-shadow duration-300 ease-in-out hover:shadow-md',
                  cardClassName
                )}
              >
                <div className="overflow-hidden">
                  <img
                    src={feature.imageUrl}
                    alt={feature.title}
                    loading="lazy"
                    className={cn(
                      'aspect-square w-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105 motion-reduce:transition-none motion-reduce:group-hover:scale-100',
                      cardImageClassName
                    )}
                  />
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between gap-3">
                    <h3
                      className={cn(
                        'text-lg font-semibold text-foreground',
                        cardTitleClassName
                      )}
                    >
                      {feature.title}
                    </h3>
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted/50 transition-colors duration-300 group-hover:bg-muted">
                      <ArrowRight
                        className="h-5 w-5 text-muted-foreground transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  {feature.subtitle && (
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                      {feature.subtitle}
                    </p>
                  )}
                </div>
              </Card>
            </motion.a>
          );
        })}
      </motion.div>
    </motion.section>
  );
}
